import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import MobileBottomNav from '../components/MobileBottomNav';
import SEOSchema from '../components/SEOSchema';
import {
  ArrowRight, ArrowLeft, RefreshCw, CheckCircle, Sparkles, Loader2, Stethoscope,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = 'https://tcubefitness.com';

// 3 guided steps
const STEPS = [
  {
    id: 'focus',
    question: 'What is your main health focus right now?',
    options: [
      { label: 'Weight, diabetes or metabolic health', value: 'metabolic' },
      { label: 'PMOS, hormones or fertility', value: 'womens' },
      { label: 'Pregnancy or postpartum recovery', value: 'pregnancy' },
      { label: 'General fitness & body transformation', value: 'fitness' },
    ],
  },
  {
    id: 'stage',
    question: 'Which best describes your situation?',
    options: [
      { label: 'Managing a diagnosed condition', value: 'condition' },
      { label: 'Trying to prevent future problems', value: 'prevent' },
      { label: 'Currently pregnant or recently delivered', value: 'maternal' },
      { label: 'Fit and healthy, want to improve', value: 'improve' },
    ],
  },
  {
    id: 'support',
    question: 'What kind of support do you want most?',
    options: [
      { label: 'Medical nutrition guidance', value: 'nutrition' },
      { label: 'Fitness coaching with safety oversight', value: 'fitness' },
      { label: 'A complete plan: nutrition + lifestyle', value: 'complete' },
      { label: 'Not sure, guide me', value: 'guide' },
    ],
  },
];

const RESULTS = {
  metabolic: {
    name: 'Diabetes & Clinical Nutrition',
    slug: '/medical-nutrition-therapy',
    hub: '/hub/metabolic-weight-health',
    reason: 'Doctor-supervised nutrition to support blood sugar, weight and metabolic health with sustainable, practical changes.',
  },
  womens: {
    name: 'Fertility & PMOS Support',
    slug: '/fertility-nutrition',
    hub: '/hub/womens-reproductive-health',
    reason: 'Personalized, doctor-guided nutrition and lifestyle care for hormonal balance, PMOS and fertility.',
  },
  pregnancy: {
    name: 'Pregnancy & Postpartum Care',
    slug: '/pregnancy-nutrition',
    hub: '/hub/pregnancy-postpartum',
    reason: 'Trimester-specific nutrition and safe postpartum recovery, supervised alongside your obstetric care.',
  },
  fitness: {
    name: 'Online Fitness Coaching',
    slug: '/online-fitness-coaching',
    hub: '/hub/fitness-body-transformation',
    reason: 'Progressive, injury-aware training with medical oversight, tailored to your body and goals.',
  },
};

const recommend = (answers) => {
  if (answers.focus === 'pregnancy' || answers.stage === 'maternal') return RESULTS.pregnancy;
  if (answers.focus === 'womens') return RESULTS.womens;
  if (answers.focus === 'metabolic' || answers.stage === 'condition') return RESULTS.metabolic;
  return RESULTS.fitness;
};

const FindMyProgramPage = () => {
  const [step, setStep] = useState(0); // 0..2 questions, 3 lead capture, 4 result
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const totalSteps = STEPS.length + 1; // + lead capture
  const progress = Math.round((Math.min(step, totalSteps) / totalSteps) * 100);
  const result = step >= 4 ? recommend(answers) : null;

  const choose = (value) => {
    const current = STEPS[step];
    setAnswers((a) => ({ ...a, [current.id]: value }));
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const finish = async (skip = false) => {
    setError('');
    if (!skip) {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        setError('Please enter a valid email, or skip this step.');
        return;
      }
      setLoading(true);
      try {
        const rec = recommend(answers);
        await axios.post(`${API}/email-leads`, {
          email,
          full_name: name || null,
          source: 'program-finder',
          program_name: rec.name,
          program_url: `${SITE}${rec.slug}`,
          hub_url: `${SITE}${rec.hub}`,
        });
      } catch (e) {
        // Non-blocking: still show the recommendation even if capture fails.
      } finally {
        setLoading(false);
      }
    }
    setStep(4);
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setName('');
    setEmail('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Find My Program: Guided Finder | TCUBE FITNESS™</title>
        <meta name="description" content="Answer three quick questions and our doctor-led program finder guides you to the right TCUBE FITNESS starting point, from metabolic health to fertility, pregnancy and fitness." />
        <link rel="canonical" href="https://tcubefitness.com/find-my-program" />
      </Helmet>

      <SEOSchema
        type="webpage"
        name="Find My Program"
        description="A short, doctor-led guided finder that routes you to the right program."
        url="/find-my-program"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Find My Program', path: '/find-my-program' }]}
      />

      <AnnouncementBanner />
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 sm:pt-36 pb-24 lg:pb-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" aria-hidden="true" />
                Guided Program Finder
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
                Find <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Your Program</span>
              </h1>
              <p className="text-slate-400 text-sm sm:text-base">Three quick questions so we can guide you to the right starting point. This is guidance, not a medical diagnosis.</p>
            </div>

            {/* Progress */}
            {step < 4 && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2 text-xs text-slate-400">
                  <span>Step {Math.min(step + 1, totalSteps)} of {totalSteps}</span>
                  <span>{progress}%</span>
                </div>
                <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            <AnimatePresence mode="wait">
              {/* Question steps */}
              {step < STEPS.length && (
                <motion.div
                  key={`q-${step}`}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.25 }}
                  className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8"
                  data-testid={`finder-step-${STEPS[step].id}`}
                >
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-6">{STEPS[step].question}</h2>
                  <div className="space-y-3">
                    {STEPS[step].options.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => choose(opt.value)}
                        className="w-full text-left bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700 hover:border-blue-500/50 rounded-xl px-4 py-3.5 text-slate-200 text-sm sm:text-base transition-all"
                        data-testid={`finder-option-${STEPS[step].id}-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {step > 0 && (
                    <button onClick={back} className="mt-5 inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm" data-testid="finder-back-btn">
                      <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" /> Back
                    </button>
                  )}
                </motion.div>
              )}

              {/* Lead capture */}
              {step === STEPS.length && (
                <motion.div
                  key="lead"
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.25 }}
                  className="bg-slate-900/60 border border-blue-500/40 rounded-2xl p-6 sm:p-8"
                  data-testid="finder-lead-capture"
                >
                  <div className="w-12 h-12 mb-4 bg-blue-500/15 rounded-2xl flex items-center justify-center border border-blue-500/30">
                    <Stethoscope className="w-6 h-6 text-blue-300" aria-hidden="true" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Your recommendation is ready</h2>
                  <p className="text-slate-400 text-sm mb-6">Enter your details to see it. We will also keep you posted with doctor-guided tips. No spam, unsubscribe anytime.</p>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name (optional)"
                      className="w-full bg-slate-900/70 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500"
                      data-testid="finder-name"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                      placeholder="Your email address"
                      className="w-full bg-slate-900/70 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500"
                      data-testid="finder-email"
                    />
                    {error && <p className="text-rose-400 text-xs" data-testid="finder-error">{error}</p>}
                    <button
                      onClick={() => finish(false)}
                      disabled={loading}
                      className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 disabled:opacity-60 text-white px-5 py-3.5 rounded-xl font-bold text-sm sm:text-base transition-all shadow-lg shadow-blue-500/25"
                      data-testid="finder-submit"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {loading ? 'Preparing…' : 'Show My Recommendation'}
                    </button>
                    <div className="flex items-center justify-between pt-1">
                      <button onClick={back} className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm" data-testid="finder-lead-back">
                        <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" /> Back
                      </button>
                      <button onClick={() => finish(true)} className="text-slate-500 hover:text-slate-300 text-sm underline underline-offset-4" data-testid="finder-skip">
                        Skip for now
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Result */}
              {step >= 4 && result && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-slate-900/60 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 text-center"
                  data-testid="finder-result-card"
                >
                  <div className="w-14 h-14 mx-auto mb-4 bg-emerald-500/15 rounded-2xl flex items-center justify-center border border-emerald-500/30">
                    <CheckCircle className="w-7 h-7 text-emerald-400" aria-hidden="true" />
                  </div>
                  <p className="text-slate-400 text-sm mb-2">Based on your answers, we recommend starting with:</p>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-testid="finder-result-name">{result.name}</h2>
                  <p className="text-slate-300 text-sm sm:text-base mb-6 max-w-md mx-auto leading-relaxed">{result.reason}</p>
                  <div className="flex flex-col sm:flex-row gap-3 items-center justify-center mb-4">
                    <Link
                      to="/book-consultation?from=finder-result"
                      className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-6 py-3 rounded-full font-semibold text-sm sm:text-base shadow-lg shadow-blue-500/25"
                      data-testid="finder-book-btn"
                    >
                      Book Free Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
                    </Link>
                    <Link
                      to={result.slug}
                      className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-6 py-3 rounded-full font-semibold text-sm"
                      data-testid="finder-program-btn"
                    >
                      View Program Details
                    </Link>
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <Link to={result.hub} className="text-blue-400 hover:text-blue-300 text-sm underline underline-offset-4" data-testid="finder-hub-link">
                      Explore the related health hub
                    </Link>
                    <button onClick={reset} className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm" data-testid="finder-retake-btn">
                      <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Start over
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <MobileBottomNav />
    </div>
  );
};

export default FindMyProgramPage;
